import {
  type CatalogRequest,
  type CompositionV3Ent,
  type CompositionV3Req,
  EntKind,
  Template,
  WorkforceType,
} from '@revelio/data-access';

import {
  type CatalogResult,
  catalogValueAt,
  readCatalogColumn,
} from './catalog-columns';

const DIVERSITY_TEMPLATE_VERSION = '0.1';
const POSITIONS_MANIFEST = 'positions_v3';

const ROLE_LEVEL_BY_ENT_KIND: Partial<Record<EntKind, number>> = {
  [EntKind.RoleLvl_1]: 1,
  [EntKind.RoleLvl_2]: 2,
  [EntKind.RoleLvl_3]: 3,
  [EntKind.RoleLvl_4]: 4,
  [EntKind.RoleLvl_5]: 5,
  [EntKind.RoleLvl_6]: 6,
  [EntKind.RoleLvl_7]: 7,
};

// grouping_id is fixed by the template: total, gender and ethnicity
const TOTAL_GROUPING = 0;
const GENDER_GROUPING = 1;
const ETHNICITY_GROUPING = 2;
const ROLE_GROUPINGS: Record<
  number,
  { dimension: 'gender' | 'ethnicity'; segmentId: number }
> = {
  3: { dimension: 'gender', segmentId: 1 },
  4: { dimension: 'gender', segmentId: 2 },
  5: { dimension: 'ethnicity', segmentId: 1 },
  6: { dimension: 'ethnicity', segmentId: 2 },
  7: { dimension: 'ethnicity', segmentId: 3 },
  8: { dimension: 'ethnicity', segmentId: 4 },
  9: { dimension: 'ethnicity', segmentId: 5 },
  10: { dimension: 'ethnicity', segmentId: 6 },
};

type DiversityCatalogSeries = {
  headcount: number[];
  share: number[];
};

type DiversityCatalogCategory = DiversityCatalogSeries & {
  id: number;
  name: string;
};

export type DiversityCatalogRole = DiversityCatalogCategory & {
  segmentId: number;
};

export type DiversityCatalogEntity = {
  index: number;
  name: string;
  total?: DiversityCatalogSeries;
  gender: DiversityCatalogCategory[];
  ethnicity: DiversityCatalogCategory[];
  rolesByGender: DiversityCatalogRole[];
  rolesByEthnicity: DiversityCatalogRole[];
};

export const isDiversityCatalogSupported = (
  entities: readonly CompositionV3Ent[]
): boolean =>
  entities.length > 0 &&
  entities.every((entity) => entity.EntType === EntKind.Company);

export const toDiversityCatalogRequest = ({
  entities,
  filters: { gender, ethnicity, ...filters },
  corrections,
  role_taxonomy,
  skill_taxonomy,
  activity_taxonomy,
  breakdown_options,
}: CompositionV3Req): CatalogRequest => ({
  template: Template.Diversity,
  version: DIVERSITY_TEMPLATE_VERSION,
  args: {
    diversity: {
      data_man: POSITIONS_MANIFEST,
      // One entity per company: ids sharing an entity are summed together.
      entities: entities
        .filter((entity) => entity.EntType === EntKind.Company)
        .map((entity) => [entity.EntID]),
      filters,
      gen_eth_weights: { gender: gender ?? [], ethnicity: ethnicity ?? [] },
      weights: {
        workforce_type: corrections.workforce_type ?? WorkforceType.FullTime,
        with_weight: corrections.with_weight ?? true,
        with_sample_weight: corrections.with_sample_weight ?? true,
      },
      taxonomies: {
        role_tax: role_taxonomy,
        skill_tax: skill_taxonomy,
        activity_tax: activity_taxonomy ?? 0,
      },
      options: {
        role_brk_level:
          ROLE_LEVEL_BY_ENT_KIND[breakdown_options.role_breakdown_level] ?? 1,
      },
    },
  },
});

export const parseDiversityCatalog = (
  catalog: CatalogResult
): DiversityCatalogEntity[] => {
  if (catalog.error) {
    throw new Error(catalog.error);
  }

  const entIndex = readCatalogColumn(
    catalog,
    'ent_index',
    'CatalogInt32Col'
  ).ints;
  const entLabel = readCatalogColumn(
    catalog,
    'ent_label',
    'CatalogArrayStringCol'
  ).stringArrays;
  const brkLabel = readCatalogColumn(
    catalog,
    'brk_label',
    'CatalogArrayStringCol'
  ).stringArrays;
  const groupingId = readCatalogColumn(
    catalog,
    'grouping_id',
    'CatalogInt32Col'
  ).ints;
  const groupId = readCatalogColumn(
    catalog,
    'group_id',
    'CatalogInt32Col'
  ).ints;
  const headcount = readCatalogColumn(
    catalog,
    'headcount',
    'CatalogArrayFloat32Col'
  ).floatArrays;
  const share = readCatalogColumn(
    catalog,
    'share',
    'CatalogArrayFloat32Col'
  ).floatArrays;

  const entities = new Map<number, DiversityCatalogEntity>();
  for (let row = 0; row < catalog.data.num_rows; row += 1) {
    const index = catalogValueAt(entIndex, row, 'ent_index');
    let entity = entities.get(index);
    if (entity === undefined) {
      entity = {
        index,
        name: catalogValueAt(entLabel, row, 'ent_label').join(', '),
        gender: [],
        ethnicity: [],
        rolesByGender: [],
        rolesByEthnicity: [],
      };
      entities.set(index, entity);
    }

    const series = {
      headcount: catalogValueAt(headcount, row, 'headcount'),
      share: catalogValueAt(share, row, 'share'),
    };
    const category = {
      ...series,
      id: catalogValueAt(groupId, row, 'group_id'),
      name: catalogValueAt(brkLabel, row, 'brk_label').join(', '),
    };
    const grouping = catalogValueAt(groupingId, row, 'grouping_id');
    switch (grouping) {
      case TOTAL_GROUPING:
        entity.total = series;
        break;
      case GENDER_GROUPING:
        entity.gender.push(category);
        break;
      case ETHNICITY_GROUPING:
        entity.ethnicity.push(category);
        break;
      default: {
        const role = ROLE_GROUPINGS[grouping];
        if (role === undefined) {
          throw new Error(`Unknown diversity catalog grouping_id ${grouping}`);
        }
        const roles =
          role.dimension === 'gender'
            ? entity.rolesByGender
            : entity.rolesByEthnicity;
        roles.push({ ...category, segmentId: role.segmentId });
      }
    }
  }

  return [...entities.values()].sort((a, b) => a.index - b.index);
};
